import * as React from "react";
import { Button, message } from "antd";
import { stringSort, numberSort } from "utils/defaultSort";
import { FormatData } from "utils/FormatData";
import { dayjs } from "Config/dayjs";
import { ColumnProps } from "antd/lib/table";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ProjectFilesSearchForm } from "./ProjectFilesSearchForm";
import { UserName } from "Components/General/UserName";
import { taskAttachmentsStore } from "../Stores/TaskAttachmentsStore";
import { Table } from "Components/General/Table";

interface ProjectFilesSearchProps extends WithTranslation {
  onExit: () => void;
  onCancel: () => void;
}

interface ProjectFilesSearchState {
  showSearchForm: boolean;
  searchResults: API_SearchResult[];
  selectedRowKeys: number[];
  loading: boolean;
}

class SearchResultsColumn extends React.Component<ColumnProps<API_SearchResult>> {}

export const ProjectFilesSearch = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<ProjectFilesSearchProps, ProjectFilesSearchState> {
      constructor(props: ProjectFilesSearchProps) {
        super(props);
        this.state = {
          showSearchForm: true,
          searchResults: [],
          selectedRowKeys: [],
          loading: false,
        };
      }

      onSelectChange = (selectedRowKeys: number[]) => {
        this.setState({ selectedRowKeys });
      };

      handleSearch = async (postObject: API_DocSearchReq) => {
        try {
          this.setState({ loading: true });
          // tslint:disable-next-line:max-line-length
          let response: API_SearchResult[] = await API.newData(
            API_URL.SEARCH.getSearchResults(projectsDataStore.projectId),
            postObject
          );
          const projectFileIds = taskAttachmentsStore
            .getFileIds()
            .filter((item) => item.indexOf("@id") === 0)
            .map((item) => parseInt(item.split("@id")[1], 10));

          response = response.filter((item) => {
            // filter already attached files
            return !projectFileIds.some((selected) => selected === item.id);
          });
          this.setState({ searchResults: response, showSearchForm: false });
        } catch (err) {
          throw err;
        } finally {
          this.setState({ loading: false });
        }
      };

      handleAddAttachments = () => {
        const { selectedRowKeys, searchResults } = this.state;
        if (!selectedRowKeys.length) {
          message.error(this.props.t("No files were selected"));
        } else {
          selectedRowKeys.forEach((selectedId) => {
            const { name, id, size } = searchResults.find((item) => item.id === selectedId);
            taskAttachmentsStore.addProjectFile({ name: name, id: id, size, type: name });
          });
          this.props.onExit();
        }
      };

      render() {
        const { t } = this.props;
        const { selectedRowKeys, searchResults, loading, showSearchForm } = this.state;
        if (showSearchForm) {
          return (
            <ProjectFilesSearchForm loading={loading} onCancel={this.props.onCancel} onSubmit={this.handleSearch} />
          );
        }
        return (
          <>
            <div className="mb-2" data-testid="project-files-search-results-table">
              <Table
                name="taskFilesSearch"
                dataSource={searchResults}
                rowKey="id"
                rowSelection={{
                  selectedRowKeys: selectedRowKeys,
                  onChange: this.onSelectChange,
                }}
              >
                <SearchResultsColumn
                  title={t("Filename")}
                  dataIndex={"name"}
                  sorter={(a, b) => stringSort(a, b, "name")}
                />
                <SearchResultsColumn
                  title={t("Size")}
                  dataIndex={"size"}
                  render={(text) => FormatData.formatBytes(text, 1)}
                  sorter={(a, b) => numberSort(a.size, b.size)}
                />
                <SearchResultsColumn
                  title={t("Last modified")}
                  dataIndex={"created"}
                  render={(text) => dayjs(+text).format("lll")}
                  sorter={(a, b) => numberSort(a.created, b.created)}
                />
                <SearchResultsColumn
                  title={t("Owner")}
                  dataIndex={"ownerId"}
                  render={(text) => <UserName id={text} />}
                />
              </Table>
            </div>
            <ButtonsRow justify="space-between">
              <Button
                onClick={this.props.onCancel}
                type="default"
                data-testid="project-files-search-results-back-button"
              >
                <FontAwesomeIcon
                  icon={["far", "chevron-left"]}
                  style={{ marginRight: 5 }}
                  fixedWidth={true}
                  size="lg"
                />
                {t("Back")}
              </Button>
              <Button
                onClick={this.handleAddAttachments}
                type="primary"
                data-testid="project-files-search-add-files-button"
              >
                {t("Add files")}
              </Button>
            </ButtonsRow>
          </>
        );
      }
    }
  )
);
